import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {
    IMatchPairQuestion,
    IElementQuestionMatchPair,
    MatchPairQuestionMode,
} from '../../../../incase-domain/elements/question/questionMatchPair';
import {IQuestionElementProps} from '../../../../incase-domain/elements/base/questionElementBase';
import {DeviceType} from '../../../../incase-domain/common/DeviceType.enum';
import {getBorderStyle} from '../../../../utils/style.utils';

interface Pair {
    leftSelectedElement: string;
    rightSelectedElement: string;
}

const uniqueByText = (items: IMatchPairQuestion[]): IMatchPairQuestion[] =>
    items.filter((item, index, self) => self.findIndex((t) => t.text === item.text) === index);

const MAX_CARD_PADDING_EM = 1;
const MIN_CARD_PADDING_EM = 0.375;

const fitCards = (container: HTMLDivElement): void => {
    const setPadding = (em: number): void => container.style.setProperty('--matchPairCardPaddingBlock', `${em}em`);
    container.classList.remove('c-matchPairQuestionCards--scroll');

    setPadding(MAX_CARD_PADDING_EM);
    const available = container.clientHeight;
    const maxHeight = container.scrollHeight;
    if (maxHeight <= available + 1) return;

    setPadding(MIN_CARD_PADDING_EM);
    const minHeight = container.scrollHeight;
    if (minHeight > available + 1) {
        container.classList.add('c-matchPairQuestionCards--scroll');
        return;
    }

    const ratio = (available - minHeight) / (maxHeight - minHeight);
    setPadding(Math.floor((MIN_CARD_PADDING_EM + ratio * (MAX_CARD_PADDING_EM - MIN_CARD_PADDING_EM)) * 1000) / 1000);
};

function MatchPairQuestionItems(props: IQuestionElementProps<IElementQuestionMatchPair>): JSX.Element | null {
    const {element, device} = props;
    const {fontSize: answerFontSize, borderRadius, fontFamily} = element;
    const cardsRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (cardsRef.current) fitCards(cardsRef.current);
    });

    useEffect(() => {
        const container = cardsRef.current;
        const layout = container?.closest('.c-questionLayout');
        if (!container || !layout) return undefined;

        const resizeObserver = new ResizeObserver(() => fitCards(container));
        resizeObserver.observe(layout);
        return () => resizeObserver.disconnect();
    }, [element.matchPairMode]);

    if (element.matchPairMode === MatchPairQuestionMode.CARDS) {
        const leftItems = uniqueByText(element.leftElement);
        const rightItems = uniqueByText(element.rightElement);
        const rows = Math.max(leftItems.length, rightItems.length);

        const renderColumn = (items: IMatchPairQuestion[], column: number): JSX.Element[] =>
            items.map((item, index) => (
                <div
                    key={item.id}
                    className="c-matchPairQuestionCards__cell"
                    style={{gridColumn: column, gridRow: `${rows - items.length + index * 2 + 1} / span 2`}}
                >
                    <div
                        className="c-matchPairQuestionCards__card"
                        style={
                            {
                                borderRadius: getBorderStyle(borderRadius),
                                fontFamily: fontFamily?.name,
                                fontSize: `${answerFontSize || 'initial'}em`,
                                textAlign: element.answerTextAlign,
                            } as React.CSSProperties
                        }
                    >
                        <span className="c-matchPairQuestionCards__text">{item.text}</span>
                    </div>
                </div>
            ));

        return (
            <div
                ref={cardsRef}
                className={`c-matchPairQuestionCards ${device === DeviceType.MOBILE ? 'c-matchPairQuestionCards--mobile' : ''}`}
            >
                {renderColumn(leftItems, 1)}
                {renderColumn(rightItems, 2)}
            </div>
        );
    }

    const generateColumn = (items: IMatchPairQuestion[], position: keyof Pair): JSX.Element => {
        const answerDataClass =
            position === 'leftSelectedElement'
                ? 'c-matchPairQuestionElement__answer_data--left'
                : 'c-matchPairQuestionElement__answer_data--right';

        const textClass =
            position === 'leftSelectedElement' ? 'c-matchPairQuestionElement__text--left' : 'c-matchPairQuestionElement__text--right';

        return (
            <div className="c-matchPairQuestionElement__answer">
                {items
                    .filter((item, index, self) => self.findIndex((t) => t.text === item.text) === index)
                    .map((item) => {
                        return (
                            <div
                                key={item.id}
                                className={`c-matchPairQuestionElement__answer_data ${answerDataClass}`}
                                style={{
                                    fontSize: `${answerFontSize || 'initial'}em`,
                                    borderRadius: getBorderStyle(borderRadius),
                                    fontFamily: fontFamily?.name,
                                }}
                            >
                                <input type="radio" id={item.id} name={position} className="c-matchPairQuestionElement__radio" disabled />
                                <button type="button" className={`c-matchPairQuestionElement__text ${textClass}`}>
                                    {item.text}
                                </button>
                            </div>
                        );
                    })}
            </div>
        );
    };

    return (
        <div className={`c-matchPairQuestionElement ${device === DeviceType.MOBILE ? 'c-matchPairQuestionElement--mobile' : ''}`}>
            {generateColumn(element.leftElement, 'leftSelectedElement')}
            {generateColumn(element.rightElement, 'rightSelectedElement')}
        </div>
    );
}

export default MatchPairQuestionItems;
